import React, { useState } from 'react';
import { Stack, Typography, Button, List, ListItem, IconButton, Avatar, useTheme, ListItemButton, Menu, MenuItem, ListItemIcon, CircularProgress, Chip } from '@mui/material';
import { Add, MoreVert, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { Part } from '@domain/product/models/ProductComponent';
import { useNavigate } from 'react-router-dom';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { useToast } from '@shared/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import EditProductPartBottomSheet from '../parts/EditProductPartBottomSheet';
import { useProductUpdate } from '@features/products/hooks/useProductUpdate';
import { Icon } from '@shared/svg-icon';

interface ProductPartsListProps {
  productId: string;
  parts: Part[] | undefined;
  onAddPart: () => void;
}

const ProductPartsList: React.FC<ProductPartsListProps> = ({
  productId,
  parts,
  onAddPart,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const { removeProductPart, isRemovingProductPart } = useProductUpdate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, part: Part) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedPart(part);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedPart(null);
  };

  const handleEdit = () => {
    if (!selectedPart?.part.id) {
      handleMenuClose();
      return;
    }
    const part = selectedPart;
    handleMenuClose();
    openBottomSheet({
      title: 'Edit Part',
      component: (
        <EditProductPartBottomSheet
          part={part}
          onSave={async (quantity) => {
            await container.productRepository.removeProductPart(productId, part.part.id!);
            await container.productRepository.addProductParts(productId, [
              { productId: part.part.id, quantity },
            ]);
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
            toast.success('Part updated');
          }}
          onClose={closeBottomSheet}
        />
      ),
      size: BottomSheetSize.SM,
    });
  };

  const handleRemove = async () => {
    if (!selectedPart?.part.id) {
      handleMenuClose();
      return;
    }
    handleMenuClose();
    await removeProductPart({ productId, partId: selectedPart.part.id! });
    queryClient.invalidateQueries({ queryKey: ['product', productId] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    toast.success('Part removed');
  };

  return (
    <BorderedBlock
      sx={{ p: 0 }}
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Parts</Typography>
          <Button endIcon={<Add />} variant="text" color="primary" onClick={onAddPart}>
            Add part
          </Button>
        </Stack>
      }
    >
      <List disablePadding>
        {(!parts || parts.length === 0) && (
          <ListItem sx={{ p: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              No parts. Add products that make up this product.
            </Typography>
          </ListItem>
        )}
        {parts?.map((part: Part) => (
          <ListItemButton
            key={part.part.id}
            onClick={() => navigate(`/products/${part.part.id}`)}
          >
            <Stack direction="row" alignItems="center" flexGrow={1} spacing={2} p={2}>
              <Avatar
                variant="rounded"
                src={part.part.image}
                sx={{
                  width: 40,
                  height: 40,
                  border: 1,
                  borderColor: theme.palette.divider,
                }}
              />
              <Typography variant="body2">{part.part.title || part.part.ean}</Typography>

            </Stack>

            <Chip
              label={`${part.quantity} ×`}
              color="info"
            />

            {isRemovingProductPart && selectedPart?.part.id === part.part.id ? (
              <CircularProgress size={20} />
            ) : (
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, part)}
                sx={{ mr: 1 }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            )}
          </ListItemButton>
        ))}
      </List>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleRemove}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography variant="body2" color="error">Remove</Typography>
        </MenuItem>
      </Menu>
    </BorderedBlock>
  );
};

export default ProductPartsList;
